import io, { type Socket } from "socket.io-client";
import { useQuery, useMutation } from "@tanstack/react-query";
import { backendApi, api, socketURL } from "./api.service";

import type { CreateThirdPartyGameSessionPayload } from "@/types";
import type {
  IFairnessPayload,
  ISharkDriftRecentSession,
} from "@/types/games/shark-drift";

import Cookies from "js-cookie";
import { PaginationProps } from "@nextui-org/react";
import {
  PaginationRequest,
  TransactionsPaginationRequest,
} from "./admin.service";
import { TransactionRequest } from "ethers";

const token = Cookies.get("jwtToken");

export const useGetAllGames = () => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-all-games"],
    queryFn: async () => {
      const response = await backendApi.get("/games", config);
      return response.data;
    },
  });
};

export const useGetAllActiveGames = () => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-all-active-games"],
    queryFn: async () => {
      const response = await backendApi.get(
        "/games/get-all-active-games",
        config,
      );
      return response.data;
    },
  });
};

export const useGetGameByName = (name: string) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-game-by-name", name],
    queryFn: async () => {
      const response = await backendApi.get(`/games/${name}`, config);
      return response.data;
    },
  });
};

export const useGetNewThirdPartyGameSession = () => {
  return useMutation({
    mutationFn: (data: CreateThirdPartyGameSessionPayload) =>
      api.post("/games/thirdParty/common/session", data),
  });
};

export const useGetSharkDriftFairness = () => {
  return useMutation({
    mutationFn: (data: IFairnessPayload) =>
      api.post("/games/sharkdrift/provable", data),
  });
};

export const useGetSummusGameURL = (gameId: string, isFunPlay?: string) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-summus-game-url", gameId],
    queryFn: async () => {
      console.log(gameId);
      const params: { isFunPlay?: string } = {};
      if (isFunPlay) {
        params.isFunPlay = isFunPlay;
      }
      const response = await backendApi.get(
        `/external/summus-games/get-game-url/${gameId}`,
        {
          ...config,
          params,
        },
      );
      return response.data;
    },
    enabled: !!gameId,
  });
};

export const useGetTadaGameURL = (
  gameId: string,
  isFunPlay?: string,
) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-tada-game-url", gameId],
    queryFn: async () => {
      console.log(gameId);
      const params: { isFunPlay?: string } = {};
      if (isFunPlay) {
        params.isFunPlay = isFunPlay;
      }
      const response = await backendApi.get(
        `/external/tada-games/get-game-url/${gameId}`,
        {
          ...config,
          params,
        },
      );
      return response.data;
    },
    enabled: !!gameId,
  });
};

export const useGetQTechGameURL = (
  gameId: string,
  isFunPlay?: string,
  isMobile?: string,
) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-qtech-game-url", gameId],
    queryFn: async () => {
      console.log(gameId);
      const params: { isFunPlay?: string; isMobile?: string } = {};
      if (isFunPlay) {
        params.isFunPlay = isFunPlay;
      }
      if (isMobile) {
        params.isMobile = isMobile;
      }
      const response = await backendApi.get(
        `/external/qtech-games/get-game-url/${gameId}`,
        {
          ...config,
          params,
        },
      );
      return response.data;
    },
    enabled: !!gameId,
  });
};

export const useGetBarbaraGameURL = (gameId: string, isFunPlay?: string) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-barbara-game-url", gameId],
    queryFn: async () => {
      console.log(gameId);
      const params: { isFunPlay?: string } = {};
      if (isFunPlay) {
        params.isFunPlay = isFunPlay;
      }
      const response = await backendApi.get(
        `/external/barbara-games/get-game-url/${gameId}`,
        {
          ...config,
          params,
        },
      );
      return response.data;
    },
    enabled: !!gameId,
  });
};

export const joinGameSocket = (game: string): Socket => {
  return io(`${socketURL}/games/${game}`, {
    extraHeaders: {
      jwtToken: token as string,
    },
  });
};

export const useGetAllPredictionRounds = (
  gameType: string,
  page: number = 1,
  pageSize: number = 3,
) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-all-prediction-rounds", gameType, page, pageSize],
    queryFn: async () => {
      const params = { page, pageSize };
      const response = await backendApi.get(
        `/games/prediction/get-all-rounds/${gameType}`,
        {
          ...config,
          params,
        },
      );
      return response.data;
    },
    enabled: !!gameType,
  });
};

export const useGetGamePrice = (gameType: string) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-game-price", gameType],
    queryFn: async () => {
      const response = await backendApi.get(
        `/games/prediction/get-price/${gameType}`,
        config,
      );
      return response.data;
    },
    enabled: !!gameType,
  });
};

type PlacePredictionBetPayload = {
  rId: string;
  amount: number | undefined;
  isPositionUp: boolean;
};

export const usePlacePredictionBet = () => {
  return useMutation({
    mutationFn: async (data: PlacePredictionBetPayload) => {
      const { rId, amount, isPositionUp } = data;
      const response = await api.post(`/games/prediction/bet/${rId}`, {
        amount,
        isPositionUp,
      });
      return response.data;
    },
  });
};

export const useGetLeaderboard = (page: number = 1, pageSize: number = 10) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-leaderboard", page, pageSize],
    queryFn: async () => {
      const params = { page, pageSize };
      const response = await backendApi.get(`/games/prediction/leaderboard`, {
        ...config,
        params,
      });
      return response.data;
    },
    // This ensures the query only runs if page and pageSize are provided
    enabled: !!page && !!pageSize,
    staleTime: 5000, // Time in milliseconds data is considered fresh
    refetchOnWindowFocus: false, // Prevents refetching when the window is refocused
  });
};

export const useGetPredictionHistory = (params: PaginationProps) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-prediction-history", params],
    queryFn: async () => {
      const response = await backendApi.get(`/games/prediction/history`, {
        ...config,
        params: { ...params },
      });
      return response;
    },
    retry: 0,
  });
};

export const useGetUserStatistics = () => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-user-statistics"],
    queryFn: async () => {
      const response = await backendApi.get(
        "/games/prediction/user-statistics",
        config,
      );
      return response?.data;
    },
  });
};

export const useGetAllGamesByCategory = (
  category: string,
  page = 1,
  pageSize?: number,
  name = "",
) => {
  return useQuery({
    queryKey: ["get-all-games-by-category", category, page, pageSize, name],
    queryFn: async () => {
      const response = await backendApi.get(
        `/games/get-all-games-by-category/${category}`,
        {
          params: { page, pageSize, name },
        },
      );
      return response.data;
    },
  });
};

export const useGetAllGamesByProvider = (
  provider: string,
  page = 1,
  pageSize?: number,
  name = "",
) => {
  return useQuery({
    queryKey: ["get-all-games-by-provider", provider, page, pageSize, name],
    queryFn: async () => {
      const response = await backendApi.get(
        `/games/get-all-games-by-provider/${provider}`,
        {
          params: { page, pageSize, name },
        },
      );
      return response.data;
    },
  });
};

export const useGetRecentGames = (
  page: number = 1,
  pageSize: number = 10,
  category?: string,
  name?: string,
) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-recent-games", page, pageSize, category, name],
    queryFn: async () => {
      const params = { page, pageSize, name, category };
      const response = await backendApi.get("/games/recent-games", {
        ...config,
        params,
      });
      return response.data;
    },
    enabled: false,
    retry: 0,
  });
};

export const useGetCreedroomzGameURL = (
  gameId: string,
  isFunPlay?: boolean,
) => {
  return useQuery({
    queryKey: ["get-creedroomz-game-url", gameId, isFunPlay],
    queryFn: async () => {
      const params = isFunPlay ? { isFunPlay } : {};

      const response = await api.get(
        `/external/creedroomz-games/get-game-url/${gameId}`,
        {
          params,
        },
      );

      return response.data;
    },
    enabled: !!gameId,
  });
};

export const useGetPascalGameURL = (gameId: string, isFunPlay?: boolean) => {
  return useQuery({
    queryKey: ["get-pascal-game-url", gameId, isFunPlay],
    queryFn: async () => {
      const params = isFunPlay ? { isFunPlay } : {};

      const response = await api.get(
        `/external/pascal-games/get-game-url/${gameId}`,
        {
          params,
        },
      );

      return response.data;
    },
    enabled: !!gameId,
  });
};

export const useGetPopOkGameURL = (gameId: string, isFunPlay?: boolean) => {
  return useQuery({
    queryKey: ["get-popok-game-url", gameId, isFunPlay],
    queryFn: async () => {
      const params = isFunPlay ? { isFunPlay } : {};

      const response = await api.get(
        `/external/popok-games/get-game-url/${gameId}`,
        {
          params,
        },
      );

      return response.data;
    },
    enabled: !!gameId,
  });
};

export const useGetUserSeedData = () => {
  return useQuery({
    queryKey: ["get-seed-data"],
    queryFn: async () => {
      const response = await api.get(`/games/get-active-seed`);

      return response.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useRotateUserSeed = () => {
  return useMutation({
    mutationFn: (data: { clientSeed: string }) => {
      return api.put(`/games/rotate-seed-pair`, data);
    },
  });
};

export const useGetSharkDriftRecentSessions = () => {
  return useQuery({
    queryKey: ["get-shark-drift-recent-sessions"],
    queryFn: async () => {
      const response = await api.get("/games/sharkdrift/recent");
      return response.data.data as ISharkDriftRecentSession[];
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetGameCounts = () => {
  return useQuery({
    queryKey: ["get-game-counts"],
    queryFn: async () => {
      const response = await api.get("/games/game-counts");
      return response.data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetGgrRevenue = (dateFrom: Date, dateTo: Date) => {
  return useQuery({
    queryKey: ["get-ggr-revenue", dateFrom, dateTo],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_GGR_API_URI}/report?startDate=${dateFrom.toISOString() || ""}&endDate=${dateTo.toISOString() || ""}`,
      );

      const resJson = await response.json();

      return {
        share: resJson.data.total,
        data: resJson.data.revenues,
      };
    },
    enabled: false,
    refetchOnWindowFocus: false,
  });
};

export const useGetGameSessionsByUsername = (
  params: {
    startDate: string;
    endDate: string;
    username: string;
  } & PaginationRequest,
) => {
  return useQuery({
    queryKey: ["admin-game-sessions"],
    queryFn: async () => {
      const response = await backendApi.get("/gamesessions/admin", {
        params,
      });

      return response;
    },
    enabled: false,
    retry: 0,
  });
};
